import { InnerBox } from "@/components/elements/InnerBox";
import { FormLabel, Grid, Stack, Typography } from "@mui/material";
import { FC, useMemo } from "react";
import { ItemInfo } from "@/components/commons/ItemInfo";
import { TLearnerForm } from "@/features/learner/types/learnerForm";
import { dayjs } from "@/utils/dayjs";
import { DATE_FORMAT, YEAR_MONTH_FORMAT } from "@/constants/datetime";
import { calculateAvailableLearningWeeks } from "@/features/learner/utils/calculates";
import { Checkbox } from "@/components/elements/Checkbox";
import { Switch } from "@/components/elements/Switch";
import { black, red, state, text } from "@/theme/colors";
import { IconWarning } from "@/assets/icons";
import { useEnum } from "@/api/services/global/enum";

import { MOCK_EXAM_WEEKS } from "@/constants/common";
import { Button } from "@/components/elements/Button/Button";
import { useInitSetup } from "@/providers/InitSetupProvider";
import { USER_TYPE } from "@/types/enum";
import { CurriculumScheduleTable } from "@/features/learner/components/CurriculumScheduleTable";
import { TIndustrySubject } from "@/api/services/main/company/dtos/get-company-list.dto";

type TLearnerCurriculumDetailProps = {
  data?: Partial<TLearnerForm>;
  subjectCount: number | null;
  industryPartTypes: string[];
  subjects: TIndustrySubject[];
  mockExamQuestionCount?: number | null;
  onOpenNewPlan: () => void;
  onViewPlanList?: () => void;
};

export const LearnerCurriculumDetail: FC<TLearnerCurriculumDetailProps> = ({
  data,
  subjectCount,
  industryPartTypes,
  subjects,
  mockExamQuestionCount,
  onOpenNewPlan,
  onViewPlanList,
}) => {
  const { data: allPartTypeOptions } = useEnum("EPartType");
  const { userType } = useInitSetup();

  const availableWeeks = calculateAvailableLearningWeeks(data?.learnerPlan?.learningStartDate, data?.learnerPlan?.examScheduledYearMonth);

  const isSupportUser = userType === USER_TYPE.SUPPORT_USER;
  const selectedPartTypes = data?.learnerPlan?.partTypes ?? [];
  const hasPartTypes = industryPartTypes.length > 0;
  const isLearningAppOff = data?.isUsingLearningApp === false;

  const partTypeCheckboxes = useMemo(() => {
    return allPartTypeOptions
      .filter((p) => industryPartTypes.includes(String(p.value)))
      .map((p) => ({
        value: String(p.value),
        label: p.label + "パート",
        checked: selectedPartTypes.includes(String(p.value)),
      }));
  }, [allPartTypeOptions, industryPartTypes, selectedPartTypes]);

  const isWeeksInsufficient = availableWeeks !== null && subjectCount !== null && subjectCount + MOCK_EXAM_WEEKS > availableWeeks;

  // Filter subjects by the learner's selected partTypes (null → all subjects). Mirrors LearnerCurriculumForm.
  const filteredSubjects = useMemo(() => {
    if (!subjects.length) return [];
    const parts = data?.learnerPlan?.partTypes;
    if (!parts) return subjects;
    return subjects.filter((s) => parts.includes(s.partType));
  }, [subjects, data?.learnerPlan?.partTypes]);

  // Schedule table shows when the learning app is ON and the industry has matching subjects.
  const showScheduleTable = !isLearningAppOff && filteredSubjects.length > 0;

  const isViewPlanListButtonDisabled = (data?.learnerPlansCount ?? 0) < 2;

  return (
    <InnerBox
      title={<Typography variant="head20Bold">受講カリキュラム</Typography>}
      showDivider={false}
      rightContent={
        <Stack direction="row" gap={2}>
          <Button variant="ghost" size="sm" onClick={onViewPlanList} disabled={isViewPlanListButtonDisabled}>
            過去の計画一覧
          </Button>
          {!isSupportUser && (
            <Button variant="outline" size="sm" onClick={onOpenNewPlan} disabled={isLearningAppOff}>
              新しいカリキュラムを計画
            </Button>
          )}
        </Stack>
      }
    >
      <Grid container rowSpacing={2} columnSpacing={3} mt={1}>
        <Grid item xs={12}>
          <Stack flexDirection="row" alignItems="center" gap={1}>
            <Typography variant="body14" color={text.primary} mr={2}>
              手続き管理を利用する
            </Typography>
            <Switch nonControl checked={!!data?.isProcedureManaged} disabled />
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <Stack flexDirection="row" alignItems="center" gap={1}>
            <Typography variant="body14" color={text.primary} mr={2}>
              学習アプリを利用する
            </Typography>
            <Switch nonControl checked={!!data?.isUsingLearningApp} disabled />
          </Stack>
        </Grid>
        <Grid item xs={6}>
          <ItemInfo
            label="カリキュラム開始日"
            value={
              data?.learnerPlan?.learningStartDate && dayjs(data.learnerPlan.learningStartDate).isValid()
                ? dayjs(data.learnerPlan.learningStartDate).format(DATE_FORMAT)
                : ""
            }
            required={!isLearningAppOff}
          />
        </Grid>
        <Grid item xs={6}>
          {hasPartTypes && (
            <>
              <FormLabel required={!isLearningAppOff} sx={{ fontSize: "12px", color: red[700] }}>
                <Typography variant="cap12" color={black[70]}>
                  受験・学習対象のパート
                </Typography>
              </FormLabel>
              <Stack direction="row" gap={2}>
                {partTypeCheckboxes.map((pt) => (
                  <Checkbox key={pt.value} label={pt.label} checked={pt.checked} disabled nonControl />
                ))}
              </Stack>
            </>
          )}
        </Grid>
        <Grid item xs={6}>
          <ItemInfo label="受験予定" value={data?.learnerPlan?.exam?.periodName ?? ""} required />
        </Grid>
        <Grid item xs={6}>
          <ItemInfo
            label="受験予定年月"
            value={
              data?.learnerPlan?.examScheduledYearMonth && dayjs(data.learnerPlan.examScheduledYearMonth).isValid()
                ? dayjs(data.learnerPlan.examScheduledYearMonth).format(YEAR_MONTH_FORMAT)
                : ""
            }
            required
          />
        </Grid>
        <Grid item xs={6}>
          <ItemInfo label="大まかな学習可能な期間（週数）" value={!isLearningAppOff && availableWeeks !== null ? `${availableWeeks}週間` : ""} />
        </Grid>
        <Grid item xs={6}>
          <ItemInfo
            label="学習に必要な期間（週数）"
            value={!isLearningAppOff && subjectCount !== null ? `${subjectCount + MOCK_EXAM_WEEKS}週間（${subjectCount} WEEK + 模擬試験）` : ""}
          />
        </Grid>
        {isWeeksInsufficient && (
          <Grid item xs={12}>
            <Stack direction="row" alignItems="center" gap={0.5}>
              <IconWarning />
              <Typography variant="cap12" color={state.warning_1}>
                受講可能な週数が科目数より少ないため、学習期間が不足する可能性があります。
              </Typography>
            </Stack>
          </Grid>
        )}
        {showScheduleTable && (
          <Grid item xs={12}>
            <CurriculumScheduleTable
              learningStartDate={data?.learnerPlan?.learningStartDate}
              subjects={filteredSubjects}
              mockExamQuestionCount={mockExamQuestionCount}
            />
          </Grid>
        )}
        {/* TODO: 自習モード - temporarily hidden
        <Grid item xs={12}>
          <Checkbox label="自習モード" checked={data?.learnerPlan?.isSelfStudyMode !== null ? data?.learnerPlan?.isSelfStudyMode : false} disabled />
          <Typography variant="cap12" color={text.secondary} ml={3.5}>学習者が自ら科目を選択して自立的に学習を進めるモードです。</Typography>
        </Grid>
        */}
      </Grid>
    </InnerBox>
  );
};
